<template>
  <div>
    <div class="mx-5">
      <div class="flex flex-row justify-between">
        <p class="self-center font-semibold text-3xl">
          {{ $route.meta.title }}
        </p>
        <vs-button
          v-show="isBluTypeDetail()"
          type="filled"
          color="secondary"
          class="w-32 font-semibold"
          @click="isLogShown = !isLogShown"
        >
          Log
        </vs-button>
      </div>
      <div class="form-container">
        <vs-row vs-w="12">
          <vs-col
            vs-w="6"
            v-for="(item, index) of BargeLineUpForm.general"
            class="p-5"
            :key="index"
          >
            <p class="font-semibold mb-3">
              {{ item.title }}
            </p>
            <select-with-add
              is-object-model
              v-if="!item.isFreeInput && (item.key === 'pod' || item.key === 'pol')"
              v-model="item.value"
              :is-valid="item.isValid"
              :options="item.key === 'pod' ? podOptions : polOptions"
              :placeholder="item.placeholder"
              :disabled="validateDisableState(item)"
              @update="(items) => updatePolPodItems(item.key, items)"
              @postUpdate="(itemName) => postUpdatePolPod(itemName, toUpper(item.key))"
              @change="changePolPod(item.key)"
            />
            <select-with-search
              is-object-model
              v-else-if="!item.isFreeInput && !Array.isArray(item.value)"
              v-model="item.value"
              :is-valid="item.isValid"
              :hide-search="!item.canSearch"
              :options="generateOptionsByKey(item.key)"
              :placeholder="item.placeholder"
              :disabled="validateDisableState(item)"
            />
            <select-with-radio
              v-else-if="!item.isFreeInput && Array.isArray(item.value) && item.isRadio"
              v-model="item.value"
              :is-valid="item.isValid"
              :options="generateOptionsByKey(item.key)"
              :placeholder="item.placeholder"
              :disabled="validateDisableState(item)"
            />
            <select-with-multiple
              v-else-if="!item.isFreeInput && Array.isArray(item.value) && !item.isRadio"
              v-model="item.value"
              :is-valid="item.isValid"
              :options="generateOptionsByKey(item.key)"
              :placeholder="item.placeholder"
              :disabled="validateDisableState(item)"
            />
            <v-autonumeric
              v-else-if="item.isFreeInput && item.isNumber"
              v-model="item.value"
              :class="[item.isValid ? 'autonumeric-primary' : 'autonumeric-danger']"
              :options="numericOptions"
              :disabled="validateDisableState(item)"
            />
            <vs-input
              v-else-if="item.isFreeInput && !item.isNumber"
              v-model="item.value"
              :color="!item.isValid ? 'danger' : ''"
              :placeholder="item.placeholder"
              :disabled="validateDisableState(item)"
            />
          </vs-col>
        </vs-row>
      </div>
      <vs-row
        v-show="companies.length"
        vs-w="12"
      >
        <vs-col
          v-for="(item, index) of productCompanies"
          :vs-w="item.key === 'jbg' || item.key === 'emb' ? 6 : 12"
          :key="index"
          :class="{'pr-2': item.key === 'jbg', 'pl-2': item.key === 'emb'}"
        >
          <div class="form-container">
            <p class="mb-3 font-semibold text-xl">
              {{ item.title }}
            </p>
            <vs-row vs-w="12">
              <vs-col
                v-for="(product, productIndex) of item.products"
                class="my-3"
                :vs-w="item.key === 'jbg' || item.key === 'emb' ? 12 : 6"
                :key="productIndex"
              >
                <!-- percentage can be `0`, so we can't use falsy -->
                <div class="flex flex-row">
                  <div
                    class="w-2/3 mr-3 flex flex-col"
                    :class="{'w-full': typeof product.percentage === 'undefined'}"
                  >
                    <div class="mb-3 flex flex-row">
                      <p class="py-1 font-semibold">
                        {{ product.title }}
                      </p>
                      <p
                        v-if="product.psy"
                        class="ml-5 px-3 py-1 self-start rounded-lg font-semibold text-center"
                        :class="validatePsyStyle(product)"
                      >
                        PSY : {{ product.psy.toLocaleString('id-ID') }}
                      </p>
                    </div>
                    <div class="flex flex-row">
                      <v-autonumeric
                        v-model="product.value"
                        class="vs-input--input"
                        :options="numericOptions"
                        :disabled="isBluTypeDetail() || item.key !== 'imm'"
                      />
                      <div class="px-5 flex flex-col justify-center rounded-r-lg bg-primary">
                        <span class="text-white">mt</span>
                      </div>
                    </div>
                  </div>
                  <!-- percentage can be `0`, so we can't use falsy -->
                  <div
                    v-if="typeof product.percentage !== 'undefined'"
                    class="w-1/3 px-3 flex flex-col"
                  >
                    <p class="mb-3 py-1 font-semibold">
                      Percentage
                    </p>
                    <div class="flex flex-row">
                      <v-autonumeric
                        v-model="product.percentage"
                        class="vs-input--input"
                        :options="numericOptions"
                        :disabled="isBluTypeDetail()"
                      />
                      <div class="px-5 flex flex-col justify-center rounded-r-lg bg-primary">
                        <span class="text-white">%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </vs-col>
            </vs-row>
          </div>
        </vs-col>

        <div
          v-if="isBluTypeEdit()"
          class="w-full"
        >
          <vs-col
            v-for="(item, index) of actualProductCompanies"
            :vs-w="item.key === 'jbg' || item.key === 'emb' ? 6 : 12"
            :key="index"
            :class="{'pr-2': item.key === 'jbg', 'pl-2': item.key === 'emb'}"
          >
            <div class="form-container">
              <p class="mb-3 font-semibold text-xl">
                Actual Tonnage
              </p>
              <vs-row vs-w="12">
                <vs-col
                  v-for="(product, productIndex) of item.products"
                  class="my-3"
                  :vs-w="item.key === 'jbg' || item.key === 'emb' ? 12 : 6"
                  :key="productIndex"
                >
                  <div class="flex flex-row">
                    <div class="w-full mr-3 flex flex-col">
                      <div class="mb-3 flex flex-row">
                        <p class="py-1 font-semibold">
                          {{ product.title }}
                        </p>
                      </div>
                      <div class="flex flex-row">
                        <v-autonumeric
                          v-model="product.value"
                          class="vs-input--input"
                          :options="numericOptions"
                        />
                        <div class="px-5 flex flex-col justify-center rounded-r-lg bg-primary">
                          <span class="text-white">mt</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </vs-col>
              </vs-row>
            </div>
          </vs-col>
        </div>
      </vs-row>
      <div
        v-show="!companies.length && thirdPartyCompany"
        class="form-container"
      >
        <div class="mb-3 flex flex-row justify-between">
          <p class="font-semibold text-xl">
            3rd party
          </p>
          <p class="py-1 px-3 rounded-lg font-semibold text-danger bg-danger bg-opacity-25">
            3rd party data will dynamic follows the column in excel
          </p>
        </div>
        <vs-row vs-w="12">
          <vs-col
            v-for="(item, index) of BargeLineUpForm.third_party_coal"
            vs-w="6"
            class="p-3"
            :key="index"
          >
            <p class="mb-3 py-1 font-semibold">
              {{ item.title }}
            </p>
            <div class="flex flex-row">
              <v-autonumeric
                v-model="item.value"
                class="vs-input--input"
                :options="numericOptions"
                :disabled="!isBluTypeEdit() && (isBluTypeDetail() || item.isDisabled)"
              />
              <div class="px-5 flex flex-col justify-center rounded-r-lg bg-primary">
                <span class="text-white">mt</span>
              </div>
            </div>
          </vs-col>
        </vs-row>
      </div>
      <div class="my-5">
        <div class="w-1/2 p-2 flex flex-row rounded-lg bg-white">
          <p
            class="w-1/2 p-3 cursor-pointer text-center"
            :class="{'shadow font-semibold rounded-lg bg-secondary text-white': actualPlanState === 'plan'}"
            @click="actualPlanState = 'plan'"
          >
            Plan
          </p>
          <p
            class="w-1/2 p-3 cursor-pointer text-center"
            :class="{'shadow font-semibold rounded-lg bg-secondary text-white': actualPlanState === 'actual'}"
            @click="actualPlanState = 'actual'"
          >
            Actual
          </p>
        </div>
      </div>
      <!-- Plan component -->
      <div
        v-show="actualPlanState === 'plan'"
        class="my-5 form-container"
      >
        <p class="mb-3 font-semibold text-xl">
          Plan
        </p>
        <vs-row vs-w="12">
          <vs-col
            v-for="(item, index) of BargeLineUpForm.plan"
            vs-w="6"
            class="p-3"
            :key="index"
          >
            <p class="mb-3 py-1 font-semibold">
              {{ item.title }}
            </p>
            <vs-input
              v-if="!item.isDate"
              v-model="item.value"
              :placeholder="item.placeholder"
              :disabled="isBluTypeDetail() ||item.isDisabled"
            />
            <date-picker
              v-else
              confirm
              v-model="item.value"
              type="datetime"
              value-type="format"
              format="DD-MMM-YYYY HH:mm"
              class="w-full"
              :placeholder="item.placeholder"
              :disabled="isBluTypeDetail() || (item.isDisabled && (bargeDetail && !bargeDetail.is_excel))"
              :disabled-date="(date) => disableDateBefore(item, date)"
              :disabled-time="(date) => disableHourBefore(item, date)"
              @change="changeDatePicker(item.key, index)"
            />
          </vs-col>
        </vs-row>
      </div>
      <!-- Actual component -->
      <div
        v-show="actualPlanState === 'actual'"
        class="my-5 form-container"
      >
        <no-data
          v-if="getBluType() === 'add'"
          title="No Data Actual"
          message="Actual data that already exist will display here"
        />
        <div v-else>
          <p class="mb-3 font-semibold text-xl">
            Actual
          </p>
          <vs-row vs-w="12">
            <vs-col
              v-for="(item, index) of BargeLineUpForm.actual"
              vs-w="6"
              class="p-3"
              :key="index"
            >
              <p class="mb-3 py-1 font-semibold">
                {{ item.title }}
              </p>
              <vs-input
                v-if="!item.isDate"
                v-model="item.value"
                :disabled="isBluTypeDetail() || item.isDisabled"
                :placeholder="item.placeholder"
              />
              <date-picker
                v-else
                confirm
                v-model="item.value"
                type="datetime"
                value-type="format"
                format="DD-MMM-YYYY HH:mm"
                class="w-full"
                :placeholder="item.placeholder"
                :disabled="isBluTypeDetail() || item.isDisabled"
                :disabled-date="(date) => disableDateBefore(item, date)"
                :disabled-time="(date) => disableHourBefore(item, date)"
              />
            </vs-col>
          </vs-row>
        </div>
      </div>
      <div class="mt-5 form-container">
        <p class="font-semibold">
          Remark
        </p>
        <vs-textarea
          v-model="remark"
          class="mt-5"
          placeholder="Enter remark"
          :disabled="isBluTypeDetail()"
        />
      </div>
      <div
        v-show="!isBluTypeDetail()"
        class="my-5 flex flex-row justify-end"
      >
        <vs-button
          type="border"
          color="secondary"
          class="w-32 mr-2 font-semibold"
          @click="$router.push({ name: 'BargeLineUpList' })"
        >
          Cancel
        </vs-button>
        <vs-button
          ref="submitButton"
          type="filled"
          color="secondary"
          class="w-32 ml-2 font-semibold vs-con-loading__container"
          @click="isBluTypeEdit() ? (isEditReasonModalShown = true) : postBargeLineUp()"
        >
          {{ isBluTypeEdit ? 'Save' : 'Submit' }}
        </vs-button>
      </div>
    </div>
    <base-custom-modal :is-shown.sync="isEditReasonModalShown">
      <template #title>
        <span class="font-semibold text-lg">Reason</span>
      </template>

      <template #body>
        <div class="flex flex-col">
          <span class="font-semibold">Reason</span>
          <textarea
            v-model="reason"
            placeholder="Enter reason here"
            class="reason-textarea"
          />
        </div>
      </template>

      <template #footer>
        <div class="my-5 flex flex-row justify-end">
          <vs-button
            type="border"
            color="secondary"
            class="w-32 mr-2 font-semibold"
            @click="isEditReasonModalShown = false"
          >
            Cancel
          </vs-button>
          <vs-button
            ref="editButton"
            type="filled"
            color="secondary"
            class="w-32 ml-2 font-semibold vs-con-loading__container"
            @click="editBargeLineUp"
          >
            Submit
          </vs-button>
        </div>
      </template>
    </base-custom-modal>
    <log-sidecar
      :is-shown.sync="isLogShown"
      :barge-log-data="bargeLogItems"
    />
    <pop-up
      :active.sync="isPopUpShown"
      @ok="closePopUp"
      @cancel="closePopUp"
    >
      You have successfully {{ isBluTypeEdit() ? 'edit' : 'add' }} barge line up
    </pop-up>
  </div>
</template>

<script>
import { isNull } from 'lodash'
import { mapGetters } from 'vuex'

import BaseCustomModal from '@/components/baseCustomModal'
import DatePicker from '@/components/datePicker'
import LogSidecar from '@/components/logSidecar'
import NoData from '@/components/noData'
import PopUp from '@/components/popUp'
import SelectWithAdd from '@/components/selectWithAdd'
import SelectWithMultiple from '@/components/selectWithMultiple'
import SelectWithRadio from '@/components/selectWithRadio'
import SelectWithSearch from '@/components/selectWithSearch'
import BargeLineUpForm from '@/fixtures/barge-line-up-form.json'
import {
  KEY_BARGE_LINE_UP_CALCULATE_DEPARTURE_TO_SMD,
  KEY_BARGE_LINE_UP_CALCULATE_LOADING_BERTHING_UNBERTHING,
  KEY_BARGE_LINE_UP_CALCULATE_PASSING_BRIDGE_TO_POD,
  KEY_BARGE_LINE_UP_CALCULATE_PASSING_BRIDGE_TO_SMDR,
  KEY_BARGE_LINE_UP_CALCULATE_UNLOADING_BERTHING_UNBERTHING,
  PAGE_TITLE
} from '@/fixtures/Constants'

export default {
  components: {
    BaseCustomModal,
    PopUp,
    SelectWithAdd,
    SelectWithMultiple,
    SelectWithSearch,
    SelectWithRadio,
    DatePicker,
    LogSidecar,
    NoData
  },
  data() {
    return {
      BargeLineUpForm,
      isLogShown: false,
      isPopUpShown: false,
      isProcessing: false,
      isDetailLoading: false,
      isEditReasonModalShown: false,
      actualPlanState: 'plan',
      remark: null,
      reason: null,
      totalProductPercentage: 0,
      totalProductQuantity: 0,
      numericOptions: {
        allowDecimalPadding: false,
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalCharacterAlternative: '.',
        roundingMethod: 'U',
        minimumValue: '0',
        modifyValueOnWheel: false
      },
      vesselOptions: [],
      bargeOptions: [],
      termOptions: [],
      tugOptions: [],
      productIdOptions: [],
      podOptions: [],
      polOptions: [],
      kpiOptions: [],
      statusOptions: [
        { value: 'Transit', text: 'Transit' },
        { value: 'Waiting To Load', text: 'Waiting To Load' },
        { value: 'Waiting At None', text: 'Waiting At None' },
        { value: 'Return Cargo', text: 'Return Cargo' },
        { value: 'Loading', text: 'Loading' },
        { value: 'Loaded Transit', text: 'Loaded Transit' },
        { value: 'Waiting Unload', text: 'Waiting Unload' },
        { value: 'Unloaded Transit', text: 'Unloaded Transit' },
        { value: 'Unloading', text: 'Unloading' },
        { value: 'Completed', text: 'Completed' }
      ],
      companies: [],
      productCompanies: [],
      actualProductCompanies: [],
      thirdPartyCompany: null,
      bargeLogItems: [],
      isProductIdEditFirstFetch: true
    }
  },
  computed: {
    ...mapGetters({
      bargeId: 'BargeLineUp/bargeId',
      bargeDetail: 'BargeLineUp/bargeDetail',
      vesselItems: 'BargeLineUp/vesselItems',
      bargeItems: 'BargeLineUp/bargeItems',
      termItems: 'BargeLineUp/termItems',
      tugItems: 'BargeLineUp/tugItems',
      productIdItems: 'BargeLineUp/productIdItems',
      thirdPartyItems: 'BargeLineUp/thirdPartyItems',
      polItems: 'BargeLineUp/polItems',
      podItems: 'BargeLineUp/podItems',
      kpiItems: 'BargeLineUp/kpiItems',
      latestPol: 'BargeLineUp/latestPol',
      latestPod: 'BargeLineUp/latestPod',
      bargeLogs: 'BargeLineUp/bargeLogs',
      portStockYardItems: 'PortStockYard/portStockYards',
      vesselLineUpDetail: 'VesselLineUp/vesselLineUpDetail'
    }),
    vesselId: () => BargeLineUpForm.general[0].value,
    productIds: () => BargeLineUpForm.general[5].value,
    pol: () => BargeLineUpForm.general[8].value,
    pod: () => BargeLineUpForm.general[9].value,
    planQuantity: () => BargeLineUpForm.general[13].value
  },
  watch: {
    isDetailLoading: {
      handler(val) {
        if (val) {
          this.showPageLoading(this)
          return
        }

        this.closePageLoading(this)
      }
    },
    vesselItems: {
      handler(val) {
        // There are `id` with `null` value, search isn't functional if there are `null` value
        this.vesselOptions = val.map(item => ({ value: item.id, text: item.name }))
          .filter(item => item.value)
      }
    },
    vesselId: {
      async handler(val) {
        await this.fetchThirdPartyItems(val.value)
        await this.fetchProductIdItems(val.value)
        this.getVesselLineUp()
      }
    },
    productIds: {
      handler(val) {
        if (val.length) {
          const selectedCompanies = this.productIdItems
            .filter(item => val.map(selected => selected.value).includes(item.id))

          const isThirdPartyCompany = selectedCompanies.some(item => !item.isCompanyProduct)
          if (isThirdPartyCompany) {
            this.thirdPartyCompany = selectedCompanies.find(item => !item.company)
            this.companies = []

            BargeLineUpForm.third_party_coal = BargeLineUpForm.third_party_coal
              .filter(item => this.thirdPartyCompany.product.toLowerCase().includes(item.key.toLowerCase()))
            return
          }

          this.companies = selectedCompanies.map(item => item.company)
          this.productCompanies = BargeLineUpForm.companies
            .filter(item => this.companies.map(company => company.toLowerCase()).includes(item.key))
          this.actualProductCompanies = this.cloneDeep(this.productCompanies)

          if (this.isBluTypeDetail() || this.isBluTypeEdit()) {
            this.parseCompanyData(this.productCompanies)
            this.parseActualCompanyData(this.actualProductCompanies)
          }

          if (!this.isBluTypeDetail()) {
            if (this.isProductIdEditFirstFetch && this.isBluTypeEdit()) {
              this.isProductIdEditFirstFetch = false
              return
            }

            this.resetEditedStatus('product_id')
            this.evaluatePlan()
          }
          return
        }

        this.thirdPartyCompany = null
        this.companies = []
      }
    },
    bargeItems: {
      handler(val) {
        // There are `id` with `null` value, search isn't functional if there are `null` value
        this.bargeOptions = val.map(item => ({ value: item.id, text: item.name }))
          .filter(item => item.value)
      }
    },
    termItems: {
      handler(val) {
        // There are `id` with `null` value, search isn't functional if there are `null` value
        this.termOptions = val.map(item => ({ value: item.id, text: item.name }))
          .filter(item => item.value)
      }
    },
    tugItems: {
      handler(val) {
        // There are `id` with `null` value, search isn't functional if there are `null` value
        this.tugOptions = val.map(item => ({ value: item.id, text: item.name }))
          .filter(item => item.value)
      }
    },
    productIdItems: {
      handler(val) {
        // There are `id` with `null` value, search isn't functional if there are `null` value
        this.productIdOptions = val.map(item => ({
          value: item.id,
          text: `${item.product} - ${item.blending_scheme}` ,
          product: item.product
        }))
          .filter(item => item.value)

        if (this.isBluTypeDetail() || this.isBluTypeEdit()) {
          this.isProductIdEditFirstFetch = true;
          this.parseProductId()
        }
      }
    },
    thirdPartyItems: {
      handler(val) {
        if (val) {
          const thirdParty = []
          Object.keys(val.third_party).forEach(key => {
            thirdParty.push({ "key": key, "title": this.toUpper(key), "isDisabled": true, "value": 0 })
          })
          BargeLineUpForm.third_party_coal = thirdParty

          if (this.isBluTypeDetail() || this.isBluTypeEdit()) {
            this.parseThirdPartyData(BargeLineUpForm.third_party_coal)
          }
        }
      }
    },
    polItems: {
      handler(val) {
        this.polOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    podItems: {
      handler(val) {
        this.podOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    kpiItems: {
      handler(val) {
        this.kpiOptions = val.map(item => ({ value: item.kpi_id, text: item.kpi_code }))
      }
    },
    pol: {
      async handler(val) {
        if (val) {
          BargeLineUpForm.general[11].value = this.getRoute()
          await this.fetchLatestPol(val.text)
        }
      }
    },
    pod: {
      async handler(val) {
        if (val) {
          BargeLineUpForm.general[11].value = this.getRoute()
          await this.fetchLatestPod(val.text, this.vesselId.text)
        }
      }
    },
    planQuantity: {
      handler(val) {
        if (this.thirdPartyCompany) {
          BargeLineUpForm.third_party_coal.forEach(product => {
            if (this.thirdPartyCompany.product.toLowerCase().includes(product.key.toLowerCase())) {
              product.value = val
            }
          })
          return
        }

        this.productCompanies = this.cloneDeep(this.productCompanies);
      }
    },
    totalProductQuantity: {
      handler(val) {
        if (val > this.planQuantity) {
          this.showFailedNotification(this, 'Product quantity cannot exceed plan quantity')
        }
      }
    },
    totalProductPercentage: {
      handler(val) {
        if (val > 100) {
          this.showFailedNotification(this, 'Product percentage cannot exceed 100%')
        }
      }
    },
    productCompanies: {
      deep: true,
      handler(val) {
        val.forEach(item => {
          item.products.forEach(product => {
            const psy = this.portStockYardItems.find(psy =>
              psy.company_name.toLowerCase() === item.key && psy.name_display === product.key
            )
            product.psy = psy ? psy.quantity : 0
            // percentage can have `0` value, so we can't use falsy
            if (product.percentage || product.percentage === 0) {
              product.value = this.planQuantity * product.percentage / 100
            }
          })
        })

        this.totalProductPercentage = val
          .map(item => item.products.reduce((sum, item) => sum += item.percentage, 0))
          .reduce((sum, item) => sum += item, 0)

        this.totalProductQuantity = val
          .map(item => item.products.reduce((sum, item) => sum += item.value, 0))
          .reduce((sum, item) => sum += item, 0)
      }
    },
    bargeDetail: {
      handler(val) {
        this.parseGeneralDetailData(BargeLineUpForm.general, val)
        this.parseDateTime(BargeLineUpForm.plan, val, 'plan')
        this.parseDateTime(BargeLineUpForm.actual, val, 'actual')
      }
    },
    bargeLogs: {
      handler(val) {
        this.bargeLogItems = val
      }
    }
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  mounted() {
    if (this.isBluTypeDetail() || this.isBluTypeEdit()) {
      this.fetchOptionsOnDetail()
      return
    }

    this.fetchOptionsOnCreate()
  },
  destroyed() {
    this.resetForm()
  },
  methods: {
    fetchOptionsOnCreate() {
      this.fetchVesselItems()
      this.fetchBargeItems()
      this.fetchTermItems()
      this.fetchTugItems()
      this.fetchPolItems()
      this.fetchPodItems()
      this.fetchKpiItems()
      this.fetchPortStockYard()
    },
    async fetchOptionsOnDetail() {
      this.isDetailLoading = true

      await this.fetchVesselItems()
      await this.fetchBargeItems()
      await this.fetchTermItems()
      await this.fetchTugItems()
      await this.fetchPolItems()
      await this.fetchPodItems()
      await this.fetchKpiItems()
      await this.fetchPortStockYard()

      await this.fetchBargeDetail()
      await this.fetchBargeLogs()
      this.isDetailLoading = false
    },
    async fetchVesselItems() {
      await this.$store.dispatch('BargeLineUp/getVesselItems')
    },
    async fetchBargeItems() {
      await this.$store.dispatch('BargeLineUp/getBargeItems')
    },
    async fetchTermItems() {
      await this.$store.dispatch('BargeLineUp/getTermItems')
    },
    async fetchTugItems() {
      await this.$store.dispatch('BargeLineUp/getTugItems')
    },
    async fetchProductIdItems(id) {
      await this.$store.dispatch('BargeLineUp/getProductIdItems', { id })
    },
    async fetchThirdPartyItems(id) {
      await this.$store.dispatch('BargeLineUp/getThirdPartyItems', { id })
    },
    async fetchPolItems() {
      await this.$store.dispatch('BargeLineUp/getPolItems')
    },
    async fetchPodItems() {
      await this.$store.dispatch('BargeLineUp/getPodItems')
    },
    async fetchKpiItems() {
      await this.$store.dispatch('BargeLineUp/getKpiItems')
    },
    async fetchLatestPol(polName) {
      await this.$store.dispatch('BargeLineUp/getLatestPol', polName)
    },
    async fetchLatestPod(podName, vesselName) {
      await this.$store.dispatch('BargeLineUp/getLatestPod', { podName, vesselName })
    },
    async fetchBargeDetail() {
      try {
        await this.$store.dispatch('BargeLineUp/getBargeDetail', this.$route.params.id || this.bargeId)
      } catch (err) {
        this.showFailedNotification(this, 'Failed to fetch BLU detail data')
      }
    },
    async fetchBargeLogs() {
      await this.$store.dispatch('BargeLineUp/getBargeLog', { id: this.$route.params.id || this.bargeId })
    },
    async fetchPortStockYard() {
      await this.$store.dispatch('PortStockYard/getPortStockYards')
    },
    async getVesselLineUp() {
      try {
        await this.$store.dispatch('VesselLineUp/getVesselLineUpDetail', BargeLineUpForm.general[0]?.value?.value)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch VLU, please try again')
      }
    },
    async postBargeLineUp() {
      if (this.validate()) {
        this.showFailedNotification(this, 'Some fields is empty, please fill it first')
        return
      }

      const dataToSubmit = this.generateSubmitData()

      try {
        this.isProcessing = true
        this.changeButtonState(this.$refs.submitButton.$el)

        await this.$store.dispatch('BargeLineUp/postBargeLineUp', dataToSubmit)

        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.isPopUpShown = true
      } catch (err) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.showFailedNotification(this, 'Failed to add BLU, please try again')
      }
    },
    async editBargeLineUp() {
      if (this.validate()) {
        this.showFailedNotification(this, 'Some fields is empty, please fill it first')
        return
      }

      try {
        let editedData = {}
        this.isProcessing = true
        this.changeButtonState(this.$refs.editButton.$el)

        const submittedData = this.generateSubmitData()

        editedData = { ...editedData, ...this.parseArrayOnEdit(submittedData) }
        editedData = { ...editedData, ...this.parseNonArrayOnEdit(submittedData) }
        editedData = { ...editedData, reason: this.reason }

        BargeLineUpForm.companies.forEach(item => {
          const index = this.actualProductCompanies.findIndex(product => product.key === item.key)
          if (index > -1) {
            editedData = {
              ...editedData,
              actual_tonnage: this.actualProductCompanies[index].products.map(product =>
                ({
                  name: product.key,
                  tonnage: product.value
                })
              )
            }
          }
        })

        await this.$store.dispatch('BargeLineUp/putBargeLineUp', editedData)
        await this.editBargeLineUpActual()

        this.isProcessing = false
        this.changeButtonState(this.$refs.editButton.$el)
      } catch (err) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.editButton.$el)
        this.showFailedNotification(this, 'Failed to update BLU, please try again')
      }
    },
    async editBargeLineUpActual() {
      try {
        let actualData = {}

        BargeLineUpForm.actual.forEach(item => {
          if (!item.isDisabled) {
            actualData = {
              ...actualData,
              [item.key]: item.value
                ? this.moment(item.value, ['DD-MMM-YYYY HH:mm']).format('YYYY-MM-DD HH:mm:ss')
                : null
            }
          }
        })

        actualData = { ...actualData, id: this.$route.params.id || this.bargeId, reason: this.reason }

        const isEveryFieldNull = Object.keys(actualData).filter((key) => key !== 'id').every((key) => isNull(actualData[key]));
        if (isEveryFieldNull) {
          this.isPopUpShown = true
          return
        }

        await this.$store.dispatch('BargeLineUp/putBargeLineUpActual', actualData)
        this.isPopUpShown = true
      } catch (err) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.editButton.$el)
        this.showFailedNotification(this, 'Failed to update actual BLU data, please try again')
      }
    },
    async postUpdatePolPod(name, type) {
      await this.$store.dispatch('BargeLineUp/postPolPodDropdown', { name, type })
    },
    validateDisableState(item) {
      switch (true) {
        case this.isBluTypeDetail():
          return true
        case item.key === 'min_cargo' || item.key === 'max_cargo':
          return this.isBluTypeEdit()
        default:
          return item.isDisabled
      }
    },
    generateSubmitData() {
      let generalData = {}
      let companyData = {}
      let thirdPartyData = {}
      let planData = {}

      BargeLineUpForm.general.forEach(item => {
        generalData = {
          ...generalData,
          [item.key]: this.parseGeneralDataForSubmit(item)
        }
        if (item.key === 'product_id') {
          generalData = {
            ...generalData,
            product_id_unique: item.value.map(arr => arr.value)
          }
        }
      })

      BargeLineUpForm.companies.forEach(item => {
        const index = this.productCompanies.findIndex(product => product.key === item.key)
        companyData = {
          ...companyData,
          [item.key]: index > -1
            ? this.productCompanies[index].products.map(product =>
              ({ name: product.key, tonnage: product.value, percentage: product.percentage || 0 })
            )
            : item.products.map(product =>
              ({ name: product.key, tonnage: product.value, percentage: product.percentage || 0 })
            )
        }
      })

      BargeLineUpForm.third_party_coal.forEach(item => {
        thirdPartyData = { ...thirdPartyData, [item.key]: item.value }
      })

      BargeLineUpForm.plan.forEach(item => {
        planData = { ...planData, [item.key]: item.value }
      })

      planData = { ...planData, type: 'Plan' }

      const barge = BargeLineUpForm.general.find(item => item.key === 'barge').value
      const tug = BargeLineUpForm.general.find(item => item.key === 'tug').value
      const kpi = BargeLineUpForm.general.find(item => item.key === 'kpi_code').value
      return {
        ...generalData,
        ...companyData,
        barge_id: barge.value,
        tug_id: tug.value,
        kpi_id: kpi.value,
        remark: this.remark,
        third_party: thirdPartyData,
        bargePlanActual: planData
      }
    },
    parseGeneralDataForSubmit(item) {
      switch (true) {
        case item.key === 'product_id':
          return item.value.map(arr => arr.product)
        case typeof item.value === 'object' && item.value !== null:
          return item.value?.text
        default:
          return item.value
      }
    },
    parseGeneralDetailData(items, detail) {
      this.remark = detail.remark

      items.forEach(item => {
        switch (item.key) {
          case 'name_of_vessel':
            item.value = this.vesselOptions.find(vessel => vessel.text === detail[item.key]) || null
            break
          case 'barge':
            item.value = this.bargeOptions.find(barge => barge.text === detail[item.key]) || null
            break
          case 'term':
            item.value = this.termOptions.find(term => term.text === detail[item.key]) || null
            break
          case 'tug':
            item.value = this.tugOptions.find(tug => tug.text === detail[item.key]) || null
            break
          case 'pol':
            item.value = this.polOptions.find(pol => pol.text === detail[item.key]) || null
            break
          case 'pod':
            item.value = this.podOptions.find(pod => pod.text === detail[item.key]) || null
            break
          case 'status':
            item.value = this.statusOptions.find(status => status.text === detail[item.key]) || null
            break
          case 'kpi_code':
            item.value = this.kpiOptions.find(kpi => kpi.text === detail[item.key]) || null
            break
          default:
            item.value = detail[item.key]
            break
        }
      })
    },
    parseProductId() {
      BargeLineUpForm.general[5].value = this.bargeDetail.product_id_unique && this.bargeDetail.product_id_unique.length
        ? this.productIdOptions.filter(productId => this.bargeDetail.product_id_unique.includes(productId.value))
        : this.productIdOptions.filter(productId => this.bargeDetail.product_id.includes(productId.product))
    },
    parseCompanyData(items) {
      items.forEach(item => {
        Object.keys(this.bargeDetail).forEach(company => {
          if (item.key === company) {
            item.products.forEach(product => {
              this.bargeDetail[company].forEach(companyProduct => {
                if (product.key === companyProduct.name) {
                  // percentage can be `0`, so we can't use falsy
                  if (typeof product.percentage !== 'undefined') {
                    product.percentage = companyProduct.percentage
                  }
                  product.value = companyProduct.tonnage
                  product.psy = companyProduct.psy
                }
              })
            })
          }
        })
      })
    },
    parseActualCompanyData(items) {
      if (this.bargeDetail.actual_tonnage && this.bargeDetail.actual_tonnage.length) {
        items.forEach(item => {
          item.products.forEach(product => {
            this.bargeDetail.actual_tonnage.forEach(actual => {
              if (product.key === actual.name) {
                product.value = actual.tonnage
              }
            })
          })
        })
      }
    },
    parseThirdPartyData(items) {
      items.forEach(item => {
        Object.keys(this.bargeDetail.third_party).forEach(key => {
          if (item.key === key) {
            item.value = this.bargeDetail.third_party[key]
          }
        })
      })
    },
    disableDateBefore(item, date) {
      switch (item.key) {
        case 'depart_smds': {
          const arriveSmds = this.moment(BargeLineUpForm.plan[0].value, ['DD-MMM-YYYY HH:mm'])
          return date < arriveSmds.subtract(1, 'days')
        }
        case 'close_bridge': {
          const openBridge = this.moment(BargeLineUpForm.plan[8].value, ['DD-MMM-YYYY HH:mm'])
          return date < openBridge.subtract(1, 'days')
        }
        case 'loading_start':
        case 'loading_end':
        case 'unloading_start':
        case 'unloading_end':
          const source = BargeLineUpForm.plan.find(plan => plan.key === item.formula.valueFrom)
          const sourceDate = this.moment(source.value, ['DD-MMM-YYYY HH:mm'])
          return sourceDate.diff(this.moment(date), 'days') > 0
      }
    },
    disableHourBefore(item, date) {
      switch (item.key) {
        case 'depart_smds': {
          const arriveSmds = this.moment(BargeLineUpForm.plan[0].value, ['DD-MMM-YYYY HH:mm'])
          if (date < arriveSmds) {
            return date.getHours() < arriveSmds.hours()
          }
          return
        }
        case 'close_bridge': {
          const openBridge = this.moment(BargeLineUpForm.plan[8].value, ['DD-MMM-YYYY HH:mm'])
          if (date < openBridge) {
            return date.getHours() < openBridge.hours()
          }
          return
        }
        case 'loading_start':
        case 'loading_end':
        case 'unloading_start':
        case 'unloading_end':
          const source = BargeLineUpForm.plan.find(plan => plan.key === item.formula.valueFrom)
          const sourceDate = this.moment(source.value, ['DD-MMM-YYYY HH:mm'])
          if (sourceDate.diff(this.moment(date), 'days') === 0) {
            return sourceDate.diff(this.moment(date), 'minutes') >= 0
          }
          return
      }
    },
    parseDateTime(items, detail, type) {
      const filteredPlanDetail = detail.bargePlanActuals.filter(plan => plan.type === this.upperFirst(type))
      if (filteredPlanDetail.length) {
        const planDetail = filteredPlanDetail[0]
        items.forEach(item => {
          Object.keys(planDetail).forEach(plan => {
            if (item.key === plan) {
              if ((plan === 'tonnage' || plan === 'barge_cycle') || !planDetail[plan]) {
                item.value = planDetail[plan]
              } else {
                item.value = this.moment(planDetail[plan], ['YYYY-MM-DDTHH:mm:ss.SSSZ']).format('DD-MMM-YYYY HH:mm')
              }
            }
          })
        })
      }
    },
    parseNonArrayOnEdit(submittedData) {
      let editedData = {}
      Object.keys(this.bargeDetail).forEach(key => {
        if (!Array.isArray(this.bargeDetail[key])) {
          switch (true) {
            case key === 'id':
              editedData = { ...editedData, [key]: this.bargeDetail[key] }
              break
            case typeof submittedData[key] === 'undefined':
              break
            case key === 'third_party':
              const filteredThirdParty =
                this.differenceWith([submittedData[key]], [this.bargeDetail[key]], this.isEqual)

              if (filteredThirdParty.length) {
                editedData = { ...editedData, [key]: filteredThirdParty[0]}
              }
              break
            case submittedData[key] !== this.bargeDetail[key]:
              editedData = { ...editedData, [key]: submittedData[key] }
              break
            default:
              break
          }
        }
      })

      return editedData
    },
    parseArrayOnEdit(submittedData) {
      let editedData = {}
      Object.keys(this.bargeDetail).forEach(key => {
        if (Array.isArray(this.bargeDetail[key])) {
          switch (key) {
            case 'bargePlanActuals': {
              if (this.bargeDetail[key].length) {
                const detailPlans = this.bargeDetail[key].filter(data => data.type === 'Plan')
                if (detailPlans.length) {
                  let parsedPlan = {}
                  Object.keys(detailPlans[0]).forEach(key => {
                    switch (true) {
                      case key === 'id' || key === 'created_at':
                        parsedPlan = { ...parsedPlan }
                        break
                      case key === 'barge_cycle' || key === 'type' || !detailPlans[0][key]:
                        parsedPlan = { ...parsedPlan, [key]: detailPlans[0][key] }
                        break
                      default:
                        parsedPlan = {
                          ...parsedPlan,
                          [key]: this.moment(detailPlans[0][key], ['YYYY-MM-DDTHH:mm:ss.SSSZ'])
                            .format('DD-MMM-YYYY HH:mm')
                        }
                        break
                    }
                  })
                  const filteredArray = this.differenceWith([submittedData.bargePlanActual], [parsedPlan], this.isEqual)

                  if (filteredArray.length) {
                    editedData = { ...editedData, bargePlanActual: filteredArray[0]}
                  }
                }
              }
              break
            }
            case 'imm': {
              const filteredArray = this.differenceBy(submittedData[key], this.bargeDetail[key], 'tonnage')

              if (filteredArray.length) {
                editedData = { ...editedData, [key]: submittedData[key] }
              }
              break
            }
            case 'product_id': {
              editedData = { ...editedData, [key]: submittedData[key] }
              break
            }
            case 'product_id_unique': {
              editedData = { ...editedData, [key]: submittedData[key] }
              break
            }
            default: {
              const filteredArray = this.differenceBy(submittedData[key], this.bargeDetail[key], 'percentage')

              if (filteredArray.length) {
                editedData = { ...editedData, [key]: submittedData[key] }
              }
              break
            }
          }
        }
      })

      return editedData
    },
    resetForm() {
      BargeLineUpForm.general.forEach(item => {
        switch (true) {
          case item.key === 'min_cargo' || item.key === 'max_cargo' || item.key === 'plan_quantity':
            item.value = 0
            break
          case Array.isArray(item.value):
            item.value = []
            break
          default:
            item.value = null
            break
        }
      })

      BargeLineUpForm.companies.forEach(item => {
        item.products.forEach(product => {
          if (product.percentage !== 'undefined') {
            product.percentage = 0
          }
          product.value = 0
          product.psy = 0
        })
      })

      BargeLineUpForm.third_party_coal = []

      BargeLineUpForm.plan.forEach(item => {
        if (item.key === 'barge_cycle') {
          item.value = 0
          return
        }
        item.value = null
      })

      BargeLineUpForm.actual.forEach(item => {
        if (item.key === 'barge_cycle') {
          item.value = 0
          return
        }
        item.value = null
      })
    },
    generateOptionsByKey(key) {
      switch (key) {
        case 'name_of_vessel':
          return this.vesselOptions
        case 'barge':
          return this.bargeOptions
        case 'term':
          return this.termOptions
        case 'tug':
          return this.tugOptions
        case 'product_id':
          return this.productIdOptions
        case 'pol':
          return this.polOptions
        case 'pod':
          return this.podOptions
        case 'status':
          return this.statusOptions
        case 'kpi_code':
          return this.kpiOptions
        default:
          return []
      }
    },
    getBluType() {
      return this.$route.meta.title.split(' ')[0].toLowerCase()
    },
    isBluTypeDetail() {
      return this.getBluType() === 'detail'
    },
    isBluTypeEdit() {
      return this.getBluType() === 'edit'
    },
    validatePsyStyle(product) {
      const psyRemain = product.psy - product.value
      switch (true) {
        case psyRemain <= 0:
          return 'bg-danger text-white'
        case psyRemain < product.value && psyRemain > 0:
          return 'bg-warning text-white'
        case psyRemain >= product.value:
          return 'bg-success text-white'
        default:
          return
      }
    },
    closePopUp() {
      this.isPopUpShown = false
      this.$router.push({ name: 'BargeLineUpList' })
    },
    updatePolPodItems(key, items) {
      if (key === 'pod') {
        this.podOptions = items
        return
      }

      this.polOptions = items
    },
    getRoute() {
      const polItem = this.polOptions.find(item => item.value === (BargeLineUpForm.general[8].value
        ? BargeLineUpForm.general[8].value.value
        : null
      ))

      const podItem = this.podOptions.find(item => item.value === (BargeLineUpForm.general[9].value
        ? BargeLineUpForm.general[9].value.value
        : null
      ))

      return `${polItem ? polItem.text : ''}-${podItem ? podItem.text : ''}`
    },
    getCalculateItem(calculate) {
      let calculateVal = '0'
      switch (calculate){
        case 'dynamicDepart':
          calculateVal = BargeLineUpForm.general[8]?.value
            ? KEY_BARGE_LINE_UP_CALCULATE_DEPARTURE_TO_SMD.find(item => item.key === BargeLineUpForm.general[8]?.value?.text.toLowerCase())
            : '0'
          break
        case 'dynamicLoading':
          calculateVal = BargeLineUpForm.general[8]?.value
            ? KEY_BARGE_LINE_UP_CALCULATE_LOADING_BERTHING_UNBERTHING.find(item => item.key === BargeLineUpForm.general[8]?.value?.text.toLowerCase())
            : '0'
          break
        case 'dynamicPassingBridgeToPod':
          calculateVal = BargeLineUpForm.general[9]?.value
            ? KEY_BARGE_LINE_UP_CALCULATE_PASSING_BRIDGE_TO_POD.find(item => item.key === BargeLineUpForm.general[9]?.value?.text.toLowerCase())
            : '0'
          break
        case 'dynamicPassingBridgeToSmdr':
          calculateVal = BargeLineUpForm.general[9]?.value
            ? KEY_BARGE_LINE_UP_CALCULATE_PASSING_BRIDGE_TO_SMDR.find(item => item.key === BargeLineUpForm.general[9]?.value?.text.toLowerCase())
            : '0'
          break
        case 'dynamicUnloading':
          calculateVal = this.calculateUnloading()
          break
        default:
          calculateVal = calculate
          break
      }
      return calculateVal?.value || calculateVal
    },
    changePolPod(key) {
      this.resetEditedStatus(key)
      this.evaluatePlan()
    },
    changeDatePicker(key, index) {
      this.resetEditedStatus(key)
      if (key === 'loading_start' || key === 'loading_end' || key === 'unloading_start' || key === 'unloading_end') {
        BargeLineUpForm.plan[index].formula.isEdited = true
      }
      this.evaluatePlan()
    },
    resetEditedStatus(key) {
      switch (key) {
        case 'depart_smds':
        case 'pol':
          BargeLineUpForm.plan[3].formula.isEdited = false
          BargeLineUpForm.plan[4].formula.isEdited = false
          break
        case 'loading_start':
          BargeLineUpForm.plan[4].formula.isEdited = false
          break
        case 'close_bridge':
        case 'pod':
        case 'product_id':
          BargeLineUpForm.plan[12].formula.isEdited = false
          BargeLineUpForm.plan[13].formula.isEdited = false
          break
        case 'unloading_start':
          BargeLineUpForm.plan[13].formula.isEdited = false
          break
      }
    },
    calculateUnloading() {
      let calculateData = ''
      const blendingScheme = this.productIdItems.find(item => item.id === BargeLineUpForm.general[5]?.value[0]?.value)?.blending_scheme?.toLowerCase()
      let method = this.vesselLineUpDetail.flf_fc_nominate?.name.toLowerCase()
      method = method === 'green calypso' ? 'fc' : method === 'shiploader' ? 'cbu' : method === 'whs iskandar' ? 'iskandar' : method
      const pod = BargeLineUpForm.general[9]?.value?.text.toLowerCase()

      if (method === 'cbu') {
        calculateData = KEY_BARGE_LINE_UP_CALCULATE_UNLOADING_BERTHING_UNBERTHING.find(x => x.key === pod)?.value?.find(x => x.key === method)
      } else {
        calculateData = KEY_BARGE_LINE_UP_CALCULATE_UNLOADING_BERTHING_UNBERTHING.find(x => x.key === pod)?.value?.find(x => x.key === method)?.value?.find(x => x.key === blendingScheme)
      }

      return calculateData || '0'
    },
    evaluatePlan() {
      BargeLineUpForm.plan.forEach(item => {
        if (item.formula) {
          const bargeCycle = item.formula.valueFrom.includes('|')
          if (bargeCycle) {
            const trips = item.formula.valueFrom.split('|')
            const sourceTripEnd = BargeLineUpForm.plan.find(plan => plan.key === trips[0])
            const sourceTripStart = BargeLineUpForm.plan.find(plan => plan.key === trips[1])

            item.value = this.moment(sourceTripEnd.value, ['DD-MMM-YYYY HH:mm']).diff(
              this.moment(sourceTripStart.value, ['DD-MMM-YYYY HH:mm']), 'days', true)
            return
          }

          if (!this.bargeDetail.is_excel && !item.formula.isEdited && (item.key !== 'loading_start' && item.key !== 'loading_end')) {
            const source = BargeLineUpForm.plan.find(plan => plan.key === item.formula.valueFrom)
            item.value = source
              ? this.moment(source.value, ['DD-MMM-YYYY HH:mm'])
                .add(this.evaluate(this.getCalculateItem(item.formula.calculate)), 'hours')
                .format('DD-MMM-YYYY HH:mm')
              : 'TBN'

            switch (item.key) {
              case 'loading_start': {
                if (this.latestPol) {
                  this.calculateQueuePlan(item, BargeLineUpForm.plan[2].value, this.latestPol.actual_plan.cast_off)
                }
                break
              }
              case 'unloading_start': {
                if (this.latestPod) {
                  this.calculateQueuePlan(item, BargeLineUpForm.plan[11].value, this.latestPod.actual_plan.cast_off_2)
                }
                break
              }
              default:
                break
            }
          }
        }
      })
    },
    calculateQueuePlan(item, calculatedValue, latestData) {
      const isArriveEarly = this.moment(calculatedValue, ['DD-MMM-YYYY HH:mm'])
        .isSameOrBefore(this.moment(latestData, ['YYYY-MM-DDTHH:mm:ss.SSSZ']),
          'minutes'
        )

      if (isArriveEarly) {
        item.value = this.moment(latestData, ['YYYY-MM-DDTHH:mm:ss.SSSZ'])
          .add(this.evaluate(this.getCalculateItem(item.formula.calculate)), 'hours')
          .format('DD-MMM-YYYY HH:mm')
      }
    },
    changeButtonState(container) {
      if (this.isProcessing) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    },
    validate() {
      BargeLineUpForm.general.forEach(item => {
        if (!item.isDisabled) {
          if (Array.isArray(item.value)) {
            item.isValid = !!item.value.length
            return
          }
          item.isValid = !!item.value
        }
      })

      return BargeLineUpForm.general.some(item => !item.isValid)
    }
  }
}
</script>

<style lang="scss" scoped>
.form-container {
  @apply my-5 p-5 rounded-lg bg-white;
}

/deep/.vs-con-input-label {
  @apply w-full;
}

/deep/.datepicker {
  @apply w-full #{!important};
}

/deep/.modal-content-container {
  @apply w-1/4 h-auto;
}

.reason-textarea {
  @apply mt-5 p-3 rounded-lg bg-white;

  min-height: 85px;
}
</style>
